import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AssetDto,
  AssetVersionDto,
  BulkUpdateAssetBodyDto,
  CreateAssetBodyDto,
  CreateAssetVersionFromScratchBodyDto,
  DisplayUser,
  MoveAssetVersionBodyDto,
  Section,
  UpdateAssetBodyDto,
  UpdateAssetVersionBodyDto,
} from '@nusign/picflow-backend-types';
import { LRUCache } from 'lru-cache';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  combineLatest,
  filter,
  firstValueFrom,
  lastValueFrom,
  map,
  merge,
  mergeWith,
  of,
  skip,
  startWith,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';
import { assetFetchCount, multiPartUploadMinSize, standardMemoryCacheExpiry } from '../consts';
import { sortAssetSection, sortRelevantFieldChanged } from '../gallery/lib/sort-asset-section';
import { GROWTHBOOK } from '../growthbook';
import { addGalleryPassword } from '../lib/add-gallery-password';
import { invertSorting } from '../lib/invert-sorting';
import { sleep } from '../lib/sleep';
import { isFreeSubscription } from '../lib/subscription-helpers';
import { unique } from '../lib/unique';
import { IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { AppConfigService } from '../providers/config.service';
import { AssetVersionService } from './asset-version.service';
import { GalleryPasswordService } from './gallery-password.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { PaginationService } from './pagination.service';
import { SectionService } from './section.service';
import { SocketService } from './socket.service';
import { StateService } from './state.service';
import { SubscriptionService } from './subscription.service';
import { TenantService } from './tenant.service';
import { UnseenActivityService } from './unseen-activity.service';
import { VirusScanService } from './virus-scan.service';

@Injectable({
  providedIn: 'root',
})
export class AssetService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(ObservableDataLoaderService);
  private sectionService = inject(SectionService);
  private tenantService = inject(TenantService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private unseenActivityService = inject(UnseenActivityService);
  private state = inject(StateService);
  private socketService = inject(SocketService);
  private subscriptionService = inject(SubscriptionService);
  private virusScanService = inject(VirusScanService);
  private assetVersionService = inject(AssetVersionService);
  private paginationService = inject(PaginationService);
  assetGalleryIdMap: Map<string, string> = new Map();
  private assetUpdateTimeMap = new Map<string, number>();

  private assetCache = new LRUCache({
    max: 1000,
    ttl: standardMemoryCacheExpiry,
  });

  onNewAssets$ = new BehaviorSubject<AssetDto[]>(null);

  private readonly onSortSection$ = new BehaviorSubject<{ sectionId: string; sorting: string }>(null);

  public readonly optimisticUpdates = new Subject<{
    name: 'asset.created' | 'asset.updated' | 'asset.deleted';
    payload: { asset: AssetDto; oldAsset?: AssetDto };
  }>();

  private allAssetEvents = this.socketService.onSocketEvent.pipe(
    filter(e => {
      if (!e?.name) return true;
      if (!e.name.startsWith('asset.')) return false;
      const asset = e.payload?.asset as AssetDto;
      if (!asset) return true;
      const lastLocalUpdate = this.assetUpdateTimeMap.get(asset.id);
      if (e.name === 'asset.updated' && lastLocalUpdate && lastLocalUpdate > asset.updated_at * 1000) {
        return false;
      }
      return true;
    }),
    map(e => ({ ...e, isOptimistic: false })),
    mergeWith(this.optimisticUpdates.pipe(map(e => ({ ...e, isOptimistic: true }))))
  );

  public readonly onAssetCreated$ = this.allAssetEvents.pipe(
    filter(e => e?.name === 'asset.created'),
    map(event => {
      return {
        asset: event.payload.asset as AssetDto,
        isOptimistic: event.isOptimistic,
        name: 'created',
      };
    }),
    startWith({ name: 'created', asset: undefined as AssetDto })
  );
  public readonly onAssetUpdated$ = this.allAssetEvents.pipe(
    filter(e => e?.name === 'asset.updated'),
    map(event => {
      const asset = event.payload.asset as AssetDto;
      const oldAsset = event.payload.oldAsset as AssetDto;
      return {
        asset,
        oldAsset,
        isOptimistic: event.isOptimistic,
        name: 'updated',
      };
    }),
    startWith({ name: 'updated', asset: undefined as AssetDto })
  );
  public readonly onAssetDeleted$ = this.allAssetEvents.pipe(
    filter(e => e?.name === 'asset.deleted'),
    map(event => {
      return {
        asset: event.payload.asset as AssetDto,
        isOptimistic: event.isOptimistic,
        name: 'deleted',
      };
    }),
    startWith({ name: 'deleted', asset: undefined as AssetDto })
  );

  public readonly updates: Observable<{
    id: string;
    action: 'updated' | 'created' | 'deleted';
    asset?: AssetDto;
    previousValue?: AssetDto;
    isOptimistic: boolean;
  }> = this.socketService.onSocketEvent.pipe(
    map(e => ({ ...e, isOptimistic: false })),
    mergeWith(this.optimisticUpdates.pipe(map(e => ({ ...e, isOptimistic: true })))),
    filter(e => {
      if (e?.name === 'asset.created') return true;
      if (e?.name === 'asset.updated') return true;
      if (e?.name === 'asset.deleted') return true;
    }),
    map(event => {
      const asset = event.payload.asset as AssetDto;
      const previousValue = event.payload.oldAsset as AssetDto;

      return {
        id: asset.id,
        action: event.name.split('.')[1] as 'updated' | 'created' | 'deleted',
        asset,
        previousValue,
        isOptimistic: event.isOptimistic,
      };
    })
  );

  private assetUpdateWatcher = this.updates.subscribe(event => {
    if (!event?.asset) return;
    this.onGetNewAssets([event.asset]);

    this.loader.updateFuzzy<AssetDto[]>(`assets_gal_`, (assets, key) => this.updateAssetsCacheFn(assets, key, event));
    this.loader.updateFuzzy<AssetDto>(`assets/${event.asset.id}`, asset => this.updateAssetCacheFn(asset, event));
  });

  private updateAssetCacheFn(asset: AssetDto, event: { action: 'updated' | 'created' | 'deleted'; asset?: AssetDto }) {
    if (!asset) return;

    if (event.action === 'updated') {
      if (event.asset.is_liked === undefined) {
        delete event.asset.is_liked;
      }
      return {
        ...asset,
        ...event.asset,
      };
    }

    return asset;
  }

  private updateAssetsCacheFn(
    assets: AssetDto[],
    key: string,
    event: { action: 'updated' | 'created' | 'deleted'; asset?: AssetDto }
  ) {
    if (!assets) return;
    const sectionId = 'sec_' + key.split('_').pop().split('-')[0];

    if (event.action === 'updated') {
      // Could be because an asset moved from one section to another
      const index = assets.findIndex(a => a.id === event.asset.id);
      if (index !== -1) {
        if (sectionId !== event.asset.section) {
          assets.splice(index, 1);
          return assets;
        }
        if (event.asset.is_liked === undefined) {
          delete event.asset.is_liked;
        }
        assets[index] = {
          ...assets[index],
          ...event.asset,
        };
        return assets;
      } else if ((event.asset.section as string) === sectionId) {
        assets.push(event.asset);
        return assets;
      }
    }

    if ((event.asset.section as string) !== sectionId) return assets;

    if (event.action === 'deleted') {
      return assets.filter(a => a.id !== event.asset.id);
    }
    if (event.action === 'created') {
      if (assets.some(a => a.id === event.asset.id)) return assets;
      assets.push(event.asset);
      return assets;
    }
    return assets;
  }

  public readonly optimisticVersionUpdates = new Subject<{
    name: 'asset-version.updated' | 'asset-version.created' | 'asset-version.deleted';
    payload: { assetVersion: AssetVersionDto; params?: UpdateAssetVersionBodyDto };
  }>();

  public readonly versionUpdates: Observable<{
    id: string;
    action: 'updated' | 'created' | 'deleted';
    assetVersion: AssetVersionDto;
  }> = this.socketService.onSocketEvent.pipe(
    mergeWith(
      this.optimisticVersionUpdates.pipe(
        map(e => {
          return {
            ...e,
            payload: {
              assetVersion: {
                ...e.payload.assetVersion,
                ...e.payload.params,
              },
            },
          };
        })
      )
    ),
    filter(e => {
      if (e?.name === 'asset-version.created') return true;
      if (e?.name === 'asset-version.updated') return true;
      if (e?.name === 'asset-version.deleted') return true; // doesn't currently get sent by the backend
    }),
    map(event => {
      const assetVersion = event.payload.assetVersion as AssetVersionDto;

      return {
        id: assetVersion.id,
        action: event.name.split('.')[1] as 'updated' | 'created' | 'deleted',
        assetVersion,
      };
    })
  );

  private versionUpdates$ = this.versionUpdates.subscribe(event => {
    if (!event?.assetVersion) return;

    this.loader.update<AssetVersionDto[]>(`asset_versions_${event.assetVersion.asset}`, versions => {
      if (!versions) return versions;

      if (event.action === 'created') {
        if (versions.some(v => v.id === event.assetVersion.id)) return versions;
        versions.push(event.assetVersion);
        return versions;
      }

      if (event.action === 'updated') {
        return versions.map(version => {
          if (version.id === event.assetVersion.id) {
            return event.assetVersion;
          }
          return version;
        });
      }

      if (event.action === 'deleted') {
        return versions.filter(v => v.id !== event.assetVersion.id);
      }
    });
  });

  private todeleteFromVirusScanner = this.virusScanService.deleteAsset.subscribe(async asset => {
    if (!asset) return;
    this.deleteAsset(asset);
  });

  private growthBook = inject(GROWTHBOOK);

  public getAsset(
    assetId: string,
    galleryId?: string,
    options: {
      noIncludes?: boolean;
      ignore401Errors?: boolean;
      allowCache?: boolean;
      includeTags?: boolean;
      includeIsLiked?: boolean;
    } = {}
  ): Observable<AssetDto> {
    if (options.allowCache) {
      const cachedAsset = this.assetCache.get(assetId) as AssetDto;
      if (cachedAsset) return of(cachedAsset);
    }

    const query = options.noIncludes ? '' : this.getIncludes(options.includeTags, options.includeIsLiked);
    const url = addGalleryPassword(
      `${this.config.apiEndpoint}/assets/${assetId}${query ? `?${query}` : ''}`,
      this.galleryPasswordService.getPassword(galleryId)
    );
    return this.loader.get(url, () => {
      return this.http
        .get<AssetDto>(url, {
          context: new HttpContext().set(IGNORE_AUTH_ERRORS, options.ignore401Errors),
        })
        .pipe(
          switchMap(asset => {
            return merge(
              this.onAssetUpdated$.pipe(
                skip(1),
                filter(event => asset.id === event.asset.id)
              ),
              this.assetVersionService.onAssetVersionUpdated$.pipe(
                skip(1),
                filter(event => asset.id === event.assetVersion.asset)
              ),
              this.onAssetDeleted$.pipe(
                skip(1),
                filter(event => asset.id === event.asset.id)
              ),
              of({ name: 'initial', asset: undefined as AssetDto })
            ).pipe(
              map(event => {
                if (event.name === 'initial') return asset;
                if ('asset' in event) {
                  return { ...asset, ...event.asset };
                } else if ('assetVersion' in event) {
                  return {
                    ...asset,
                    version_data: <AssetVersionDto>{
                      ...asset.version_data,
                      ...event.assetVersion,
                    },
                  };
                }
              })
            );
          })
        );
    });
  }

  public getAssetCount(galleryId: string, sectionId: string) {
    return this.loader.get(
      'assets_count_' + galleryId + sectionId,
      () => {
        return this.http
          .get<{
            count: number;
          }>(
            addGalleryPassword(
              `${this.config.apiEndpoint}/assets/count?section=${sectionId}&gallery=${galleryId}&limit=1`,
              this.galleryPasswordService.getPassword(galleryId)
            )
          )
          .pipe(map(resp => resp.count));
      },
      standardMemoryCacheExpiry,
      false
    );
  }

  public getSectionAssets(
    sectionId: string,
    galleryId: string,
    options: { ignore401Errors?: boolean; includeTags?: boolean; includeIsLiked?: boolean } = {}
  ) {
    return this.loader.get(
      `assets_${galleryId}_${sectionId}${options.includeTags ? '-with-tags' : ''}${options.includeIsLiked ? '-with-likes' : ''}`,
      () => {
        const url = addGalleryPassword(
          `${this.config.apiEndpoint}/assets?section=${sectionId}&gallery=${galleryId}${this.getIncludes(options.includeTags, options.includeIsLiked)}`,
          this.galleryPasswordService.getPassword(galleryId)
        );
        return this.http
          .get<{ data: AssetDto[] }>(url, {
            context: new HttpContext().set(IGNORE_AUTH_ERRORS, options.ignore401Errors),
            transferCache: false,
          })
          .pipe(
            map(resp => {
              const assets = resp.data;
              assets.sort((a, b) => a.position - b.position);
              this.onGetNewAssets(assets);
              return assets;
            })
          );
      },
      standardMemoryCacheExpiry,
      false
    );
  }

  public getAssetsPaginated(
    sectionId: string,
    galleryId: string,
    sorting: Observable<string>,
    next: BehaviorSubject<void>,
    options: {
      ignore401Errors?: boolean;
      limit?: number;
      includeIsLiked?: boolean;
    } = {}
  ): Observable<{ assets: AssetDto[]; has_more_next: boolean }> {
    let allAssets: AssetDto[] = [];

    let sort: string;

    let sortingSkipFetch = false;
    let cachedHasMoreNext: boolean = undefined;

    return sorting.pipe(
      filter(Boolean),
      switchMap(_sort => {
        sort = _sort;
        if (sortingSkipFetch) {
          sortingSkipFetch = false;
          return of(cachedHasMoreNext);
        }

        allAssets = [];
        return next.pipe(
          switchMap(() => {
            const lastAsset = allAssets[allAssets.length - 1];
            const pageAfter = this.paginationService.encodePageInfo(lastAsset, [sort]);

            const url = addGalleryPassword(
              `${this.config.apiEndpoint}/assets?section=${sectionId}&gallery=${galleryId}${this.getIncludes(false, options.includeIsLiked)}${pageAfter ? `&page_after=${pageAfter}` : ''}${`&limit=${options.limit || assetFetchCount}`}${sort ? `&sort[]=${sort}` : ''}`,
              this.galleryPasswordService.getPassword(galleryId)
            );

            return this.http
              .get<{ data: AssetDto[]; has_more_next: boolean }>(url, {
                context: new HttpContext().set(IGNORE_AUTH_ERRORS, options.ignore401Errors),
                transferCache: false,
              })
              .pipe(
                map(resp => {
                  const assets = resp.data;
                  allAssets.push(...assets);
                  allAssets = unique(allAssets, 'id', true);
                  this.onGetNewAssets(assets);
                  return resp.has_more_next;
                })
              );
          })
        );
      }),
      switchMap(has_more_next => {
        cachedHasMoreNext = has_more_next;
        return merge(
          this.onAssetUpdated$.pipe(
            skip(1),
            filter(event => {
              if (event.asset.section === sectionId) return true;
              return allAssets.some(f => f.id === event.asset.id);
            })
          ),
          this.assetVersionService.onAssetVersionUpdated$.pipe(
            skip(1),
            filter(event => {
              return allAssets.some(f => f.id === event.assetVersion.asset);
            })
          ),
          this.onAssetDeleted$.pipe(
            skip(1),
            filter(event => {
              return event.asset.section === sectionId;
            })
          ),
          this.onAssetCreated$.pipe(
            skip(1),
            filter(event => {
              return event.asset.section === sectionId;
            })
          ),
          this.onSortSection$.pipe(
            skip(1),
            filter(event => event.sectionId === sectionId),
            map(event => {
              return {
                name: 'sorting',
                asset: undefined as AssetDto,
                sorting: event.sorting,
              };
            })
          ),
          of({ name: 'initial', asset: undefined as AssetDto })
        ).pipe(
          map(event => {
            if (event.name === 'initial') return { assets: allAssets, has_more_next, changed: true };
            let changed = false;
            const updatedAsset = event.name === 'updated' && 'asset' in event ? event.asset : undefined;
            const updatedAssetVersion =
              event.name === 'updated' && 'assetVersion' in event ? event.assetVersion : undefined;
            const deletedAsset = event.name === 'deleted' ? event.asset : undefined;
            const createdAsset = event.name === 'created' ? event.asset : undefined;

            const shouldAddAsset = (asset: AssetDto) => {
              if (allAssets.some(f => f.id === asset.id)) return false;
              if (!cachedHasMoreNext) return true; // if we have already loaded all assets, we should always add the new asset
              const sortingCheck = sortAssetSection([allAssets[allAssets.length - 1], asset], sort); // using only the last asset instead of all assets for performance, should have the same result
              return sortingCheck[0].id === asset.id;
            };

            const getInsertIndex = (asset: AssetDto) => {
              const sortedAssets = sortAssetSection([...allAssets, asset], sort);
              return sortedAssets.findIndex(f => f.id === asset.id);
            };

            if (updatedAsset) {
              const oldAsset = allAssets.find(f => f.id === updatedAsset.id);
              if (oldAsset) {
                changed = true;
                const assetMovedSection = updatedAsset.section !== oldAsset.section;
                if (assetMovedSection) {
                  allAssets = allAssets.filter(g => g.id !== updatedAsset.id);
                } else {
                  const index = allAssets.findIndex(f => f.id === updatedAsset.id);
                  if (index > -1) {
                    const sortingCheck = sortAssetSection([allAssets[allAssets.length - 1], updatedAsset], sort);
                    if (sortingCheck[0].id !== updatedAsset.id && has_more_next) {
                      // if the updated asset is at the end of all assets, and we don't already have all assets loaded, then we shouldn't move it there.
                      // We can expect that we will get this asset again in future pagination requests
                      allAssets.splice(index, 1);
                    } else {
                      const reSort = sortRelevantFieldChanged(sort, allAssets[index], updatedAsset);
                      allAssets[index] = { ...allAssets[index], ...updatedAsset };
                      if (reSort) {
                        allAssets = sortAssetSection(allAssets, sort);
                      }
                    }
                  }
                }
              } else if ('asset' in event && event.asset.section === sectionId) {
                if (shouldAddAsset(updatedAsset)) {
                  changed = true;
                  allAssets.splice(getInsertIndex(updatedAsset), 0, updatedAsset);
                }
              }
            }

            if (updatedAssetVersion) {
              const index = allAssets.findIndex(f => f.id === updatedAssetVersion.asset);
              if (index > -1) {
                changed = true;
                allAssets[index] = {
                  ...allAssets[index],
                  version_data: <AssetVersionDto>{
                    ...allAssets[index].version_data,
                    ...updatedAssetVersion,
                  },
                };
              }
            }

            if (deletedAsset) {
              allAssets = allAssets.filter(f => f.id !== deletedAsset.id);
              changed = true;
            }
            if (createdAsset && shouldAddAsset(createdAsset)) {
              allAssets.splice(getInsertIndex(createdAsset), 0, createdAsset);
              changed = true;
            }

            if (event.name === 'sorting' && 'sorting' in event) {
              // When we switch to position sorting, we do a call to let the backend update the position fields.
              // This code here is to do this optimistically.

              const sortedAssets = sortAssetSection(allAssets, event.sorting);
              sortedAssets.forEach((_asset, index) => {
                const asset = allAssets.find(f => f.id === _asset.id);
                this.optimisticUpdateAsset(asset, { position: index });
                asset.position = index;
              });
              // We want to avoid the sorting change to position, as that will re-fetch all assets
              // which would undo the optimistic sorting we just did.
              sortingSkipFetch = true;
              allAssets = sortAssetSection(allAssets, sort);
              changed = true;
            }

            return {
              assets: allAssets,
              has_more_next,
              changed,
            };
          }),
          filter(({ changed }) => changed)
        );
      })
    );
  }

  private getNextSection(sections: Section[], sectionId: string, direction: 'before' | 'after'): Section {
    const assetSections = sections.filter(s => s.object === 'asset').sort((a, b) => a.position - b.position);
    const sectionIndex = assetSections.findIndex(s => s.id === sectionId);
    if (direction === 'before') {
      if (sectionIndex < 0) return;
      return assetSections[sectionIndex - 1];
    }
    if (sectionIndex === assetSections.length - 1) return;
    return assetSections[sectionIndex + 1];
  }

  private getFirstAssets(
    sectionId: string,
    galleryId: string,
    sorting: string,
    limit: number,
    options: { includeIsLiked?: boolean } = {}
  ) {
    const url = addGalleryPassword(
      `${this.config.apiEndpoint}/assets?section=${sectionId}&gallery=${galleryId}${this.getIncludes(false, options.includeIsLiked)}${`&limit=${limit}`}&sort[]=${sorting}`,
      this.galleryPasswordService.getPassword(galleryId)
    );

    return this.http
      .get<{ data: AssetDto[]; has_more_next: boolean }>(url, {
        transferCache: false,
      })
      .pipe(map(res => res.data));
  }

  /**
   * Fetches additional assets from a gallery section, ensuring the desired count of assets is met.
   * If the current section does not have enough assets, it recursively fetches assets from adjacent sections
   * in the specified direction ('before' or 'after').
   *
   * @returns An observable that emits the list of assets once the desired count is met.
   */
  private fetchAdditionalAssets(
    sectionId: string,
    galleryId: string,
    assets: AssetDto[],
    limit: number,
    sections: Section[],
    direction: 'before' | 'after',
    checkedSections: string[] = [],
    options: { includeIsLiked?: boolean } = {}
  ): Observable<AssetDto[]> {
    // We didn't get enough assets, so we need to fetch assets of the previous/next section
    const nextSection = this.getNextSection(sections, sectionId, direction);
    if (!nextSection) return of(assets);

    // Checking if we've already checked this section to avoid infinite loops
    if (checkedSections.includes(nextSection.id)) return of(assets);
    checkedSections.push(nextSection.id);
    // Getting the first/last assets of the previous/next section
    const sorting = direction === 'before' ? invertSorting(nextSection.sorting) : nextSection.sorting;
    return this.getFirstAssets(nextSection.id, galleryId, sorting, limit, options).pipe(
      map(_assets => _assets.filter(a => !assets.some(_a => _a.id === a.id))),
      switchMap(_assets => {
        assets = direction === 'before' ? [..._assets, ...assets] : [...assets, ..._assets];
        if (assets.length >= limit) return of(assets);
        return this.fetchAdditionalAssets(
          nextSection.id,
          galleryId,
          assets,
          limit,
          sections,
          direction,
          checkedSections,
          options
        );
      })
    );
  }

  public getNextAssets(
    asset: AssetDto,
    sectionId: string,
    sections: Section[],
    galleryId: string,
    limit: number,
    direction: 'before' | 'after',
    options: {
      includeIsLiked?: boolean;
    } = {}
  ) {
    // Setting up initial request for more assets
    const sorting = sections.find(s => s.id === sectionId).sorting;
    const pageInfo = this.paginationService.encodePageInfo(asset, [sorting]);
    const url = addGalleryPassword(
      `${this.config.apiEndpoint}/assets?section=${sectionId}&gallery=${galleryId}${this.getIncludes(false, options.includeIsLiked)}&page_${direction}=${pageInfo}&limit=${limit}&sort[]=${sorting}`,
      this.galleryPasswordService.getPassword(galleryId)
    );
    // Initial request for more assets
    return this.http.get<{ data: AssetDto[]; has_more_next: boolean }>(url, { transferCache: false }).pipe(
      switchMap(res => {
        const assets = res.data;
        // If the initial request returned enough assets, we can return them
        if (assets.length >= limit) return of(assets);
        // If not, fetch additional assets (recursively)
        return this.fetchAdditionalAssets(sectionId, galleryId, assets, limit, sections, direction);
      }),
      tap(assets => {
        this.onGetNewAssets(assets);
      })
    );
  }

  public getAssetsForGalleryObservable(
    galleryId: string,
    options: { ignore401Errors?: boolean; sorted?: boolean; includeTags?: boolean; includeIsLiked?: boolean } = {}
  ) {
    return this.sectionService.getSections(galleryId as string).pipe(
      switchMap(sections => {
        const assetSections = sections.filter(section => section.object === 'asset');
        if (!assetSections.length) return of([] as AssetDto[]);
        return combineLatest(
          assetSections.map(section =>
            this.getSectionAssets(section.id, section.gallery, options).pipe(
              map(assets => {
                if (!options.sorted) return assets;
                return sortAssetSection(assets, section.sorting);
              })
            )
          )
        );
      }),
      map(assets => assets.flat())
    );
  }

  public async getAssetsForGallery(
    galleryId: string,
    options: { ignore401Errors?: boolean; sorted?: boolean; includeTags?: boolean; includeIsLiked?: boolean } = {}
  ): Promise<AssetDto[]> {
    return await firstValueFrom(this.getAssetsForGalleryObservable(galleryId, options));
  }

  public async deleteAsset(asset: AssetDto) {
    if (!asset) return;

    this.assetUpdateTimeMap.set(asset.id, Date.now());
    this.optimisticUpdates.next({
      name: 'asset.deleted',
      payload: {
        asset,
      },
    });
    await sleep(0);
    return await lastValueFrom(
      this.http.delete(
        addGalleryPassword(
          `${this.config.apiEndpoint}/assets/${asset.id}`,
          this.galleryPasswordService.getPassword(asset.gallery as string)
        )
      )
    );
  }

  public deleteAssets(assets: AssetDto[]) {
    assets.forEach(asset => {
      this.assetUpdateTimeMap.set(asset.id, Date.now());
      this.optimisticUpdates.next({
        name: 'asset.deleted',
        payload: {
          asset,
        },
      });
    });
    const galleryId = assets[0]?.gallery as string;
    const url = addGalleryPassword(
      `${this.config.apiEndpoint}/assets/bulk`,
      this.galleryPasswordService.getPassword(galleryId)
    );

    return this.http.request('DELETE', url, { body: { assets: assets.map(a => a.id) } });
  }

  public optimisticUpdateAsset(asset: AssetDto, params: Partial<AssetDto>) {
    this.assetUpdateTimeMap.set(asset.id, Date.now());
    this.optimisticUpdates.next({
      name: 'asset.updated',
      payload: {
        asset: {
          ...asset,
          ...params,
        },
        oldAsset: asset,
      },
    });
  }

  public async updateAsset(asset: AssetDto, params: UpdateAssetBodyDto, galleryId: string, noOptimistic = false) {
    if (!asset) return;
    if (!noOptimistic) this.optimisticUpdateAsset(asset, params);

    return await lastValueFrom(
      this.http.patch(
        addGalleryPassword(
          `${this.config.apiEndpoint}/assets/${asset.id}`,
          this.galleryPasswordService.getPassword(galleryId)
        ),
        params
      )
    );
  }

  public updateAssets(assets: AssetDto[], params: Partial<BulkUpdateAssetBodyDto['payload']>) {
    assets.forEach(asset => {
      this.optimisticUpdateAsset(asset, params);
    });
    const galleryId = assets[0]?.gallery as string;

    const url = addGalleryPassword(
      `${this.config.apiEndpoint}/assets/bulk`,
      this.galleryPasswordService.getPassword(galleryId)
    );

    return this.http.patch(url, <BulkUpdateAssetBodyDto>{
      payload: params,
      assets: assets.map(a => a.id),
    });
  }

  public updateAssetPositions(items: { asset: AssetDto; position: number }[]) {
    items.forEach(item => {
      this.optimisticUpdateAsset(item.asset, { position: item.position });
    });
    const galleryId = items[0]?.asset.gallery as string;

    const url = addGalleryPassword(
      `${this.config.apiEndpoint}/assets/bulk/positions`,
      this.galleryPasswordService.getPassword(galleryId)
    );

    return this.http.patch(
      url,
      items.map(i => ({ asset: i.asset.id, position: i.position }))
    );
  }

  public likeAssets(assets: AssetDto[], galleryId: string) {
    assets.forEach(asset => this.optimisticUpdateAsset(asset, { is_liked: true, likes: asset.likes + 1 }));

    const url = addGalleryPassword(
      `${this.config.apiEndpoint}/assets/like/bulk`,
      this.galleryPasswordService.getPassword(galleryId)
    );

    return this.http.post(url, { assets: assets.map(a => a.id) }).pipe(
      tap(() => {
        assets.forEach(asset => this.loader.forceDataRefresh(`assetLikeActivity_${asset.id}`));
      })
    );
  }

  public unlikeAssets(assets: AssetDto[], galleryId: string) {
    assets.forEach(asset => this.optimisticUpdateAsset(asset, { is_liked: false, likes: asset.likes - 1 }));

    const url = addGalleryPassword(
      `${this.config.apiEndpoint}/assets/like/bulk`,
      this.galleryPasswordService.getPassword(galleryId)
    );

    return this.http.request('DELETE', url, { body: { assets: assets.map(a => a.id) } }).pipe(
      tap(() => {
        assets.forEach(asset => this.loader.forceDataRefresh(`assetLikeActivity_${asset.id}`));
      })
    );
  }

  private calcUploadType(contentLength: number) {
    if (contentLength <= multiPartUploadMinSize) return 'post';
    return 'multipart';
  }

  public async postAsset(data: {
    galleryId: string;
    sectionId: string;
    contentLength: number;
    position: number;
    name: string;
    description?: string;
  }): Promise<AssetDto> {
    const params: CreateAssetBodyDto = {
      gallery: data.galleryId,
      section: data.sectionId,
      asset_name: data.name.replace(/#/, ''),
      position: data.position,
      description: data.description || undefined,
      content_length: data.contentLength,
      upload_type: this.calcUploadType(data.contentLength),
      accelerated: this.growthBook.isOn('accelerated-upload'),
    };

    if (this.config.env === 'production') {
      // temporary fix
      params['visibility'] = 'public';
    }
    const asset = await lastValueFrom(this.http.post<AssetDto>(`${this.config.apiEndpoint}/assets`, params));

    this.assetUpdateTimeMap.set(asset.id, Date.now());
    this.optimisticUpdates.next({
      name: 'asset.created',
      payload: {
        asset: asset,
      },
    });
    return asset;
  }

  public async postVersion(
    asset: AssetDto,
    data: {
      contentLength: number;
      name: string;
      galleryId: string;
    },
    isFree: boolean
  ) {
    const params: CreateAssetVersionFromScratchBodyDto = {
      asset: asset.id,
      asset_name: data.name.replace(/#/, ''),
      content_length: data.contentLength,
      upload_type: this.calcUploadType(data.contentLength),
      accelerated: this.growthBook.isOn('accelerated-upload'),
    };

    const version = await this.calcNextVersionNumber(asset.id, data.galleryId, isFree);
    params.version_name = `v${version}`;

    const resp = <AssetVersionDto>(
      await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/asset_versions`, params))
    );
    this.optimisticVersionUpdates.next({
      name: 'asset-version.created',
      payload: {
        assetVersion: resp,
      },
    });
    return resp;
  }

  public applySorting(sectionId: string, galleryId: string, sorting: string) {
    this.onSortSection$.next({ sectionId, sorting });

    if (!sorting.endsWith('capture_time')) {
      return this.http.post(`${this.config.apiEndpoint}/sections/${sectionId}/assets/change/position`, {
        sorting,
      });
    }
    // need to update the positions ourselves as the backend doesn't support it
    return this.getSectionAssets(sectionId, galleryId).pipe(
      take(1),
      switchMap(assets => {
        const sortedAssets = sortAssetSection(assets, sorting);
        return this.updateAssetPositions(sortedAssets.map((asset, index) => ({ asset, position: index })));
      }),
      // this timer prevents multiple bulk requests from being processed at the same time, which would cause conflicts and weird websocket message orders
      // In the future, this should be replaced by waiting for the first bulk request to be completed
      switchMap(() => timer(10_000))
    );
  }

  public async copyAsset(assetId: string, toGalleryId: string, toSectionId: string) {
    const params: CreateAssetBodyDto = {
      copy_from: assetId,
      gallery: toGalleryId,
      section: toSectionId,
    };
    return lastValueFrom(this.http.post(`${this.config.apiEndpoint}/assets`, params));
  }

  public async copyAssets(assetIds: string[], toGalleryId: string, toSectionId: string) {
    const params = {
      mode: 'sync',
      allSettled: true,
      operations: assetIds.map(assetId => ({
        api: 'asset',
        method: 'create',
        payload: {
          body: {
            copy_from: assetId,
            gallery: toGalleryId,
            section: toSectionId,
          },
        },
      })),
    };

    const resp = await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/bulk`, params));
    return resp as { results: AssetDto[] };
  }

  public async makeVersionOutOfAsset(fromAssetId: string, toAssetId: string, toGalleryId: string) {
    const subscription = await firstValueFrom(this.subscriptionService.getSubscription());
    const version = await this.calcNextVersionNumber(toAssetId, toGalleryId, isFreeSubscription(subscription));
    const params: MoveAssetVersionBodyDto = {
      move_from: fromAssetId,
      move_to: toAssetId,
    };
    const resp = await lastValueFrom(
      this.http.post<AssetVersionDto>(`${this.config.apiEndpoint}/asset_versions`, params)
    );
    this.optimisticVersionUpdates.next({
      name: 'asset-version.created',
      payload: {
        assetVersion: resp,
      },
    });
    await this.updateAssetVersion(resp, { version_name: `v${version}` });
  }

  public async calcNextVersionNumber(assetId: string, galleryId: string, isFree: boolean) {
    const versions = (await firstValueFrom(this.getAssetVersions(assetId, galleryId))).slice();

    versions.sort(
      (a, b) => parseInt((a.version_name || 'v1').substring(1)) - parseInt((b.version_name || 'v1').substring(1))
    );

    const version = isFree ? 1 : parseInt((versions.pop()?.version_name || 'v1').substring(1)) + 1;

    return version;
  }

  public async deleteAssetVersion(version: AssetVersionDto) {
    this.optimisticVersionUpdates.next({
      name: 'asset-version.deleted',
      payload: {
        assetVersion: version,
      },
    });
    const resp = await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/asset_versions/${version.id}`));
    this.tenantService.refreshTenant();
    return resp;
  }

  public async deleteAllAssetVersions(galleryId?: string) {
    if (galleryId) {
      await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/galleries/${galleryId}/asset_versions`));
    } else {
      await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/asset_versions`));
    }
    this.loader.forceDataRefreshFuzzy('asset_versions_');
    return true;
  }

  public async updateAssetVersion(version: AssetVersionDto, params: UpdateAssetVersionBodyDto) {
    this.optimisticVersionUpdates.next({
      name: 'asset-version.updated',
      payload: {
        assetVersion: version,
        params,
      },
    });
    const resp = await lastValueFrom(
      this.http.patch<AssetVersionDto>(`${this.config.apiEndpoint}/asset_versions/${version.id}`, params)
    );
    return resp;
  }

  public getCreator(assetVersionId: string, galleryId: string): Observable<DisplayUser> {
    return this.http
      .get<
        AssetVersionDto & { created_by: DisplayUser }
      >(addGalleryPassword(`${this.config.apiEndpoint}/asset_versions/${assetVersionId}?expand[]=created_by`, this.galleryPasswordService.getPassword(galleryId)))
      .pipe(
        map(resp => resp.created_by),
        catchError(() => of(undefined))
      );
  }

  public async handleOrderChange(assets: AssetDto[]) {
    const changes = this.calcOrderChanges(assets);
    await firstValueFrom(this.updateAssetPositions(changes.map(c => ({ asset: c.asset, position: c.pos }))));
    return changes;
  }

  public calcOrderChanges(assets: AssetDto[]): { asset: AssetDto; pos: number }[] {
    // if there are duplicates (positions) we just send the whole set of assets to be changed
    if (assets.some(asset => assets.some(i => i.position === asset.position && i.id !== asset.id))) {
      return assets.map((asset, i) => {
        return { asset, pos: i };
      });
    }

    // Otherwise we figure out the changes and send only those
    const assetsInPreviousOrder = assets.slice().sort((a, b) => a.position - b.position);
    return assets
      .map((asset, i) => {
        const oldAssetInPos = assetsInPreviousOrder[i];
        if (oldAssetInPos.id !== asset.id) return { asset, pos: oldAssetInPos.position };
      })
      .filter(a => a);
  }

  /**
   * Not ready yet. Backend throws an error for some reason
   * @param asset
   */
  public async assetView(asset: AssetDto) {
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/assets/${asset.id}/view`, {}));
  }

  public getAssetVersions(assetId: string, galleryId?: string): Observable<AssetVersionDto[]> {
    return this.loader.get(
      'asset_versions_' + assetId,
      () => {
        return this.http
          .get<{
            data: AssetVersionDto[];
          }>(
            addGalleryPassword(
              `${this.config.apiEndpoint}/asset_versions?asset=${assetId}`,
              this.galleryPasswordService.getPassword(galleryId)
            )
          )
          .pipe(map(resp => resp.data));
      },
      standardMemoryCacheExpiry,
      false
    );
  }

  public withUpdates(assets: AssetDto[]) {
    return merge(
      this.onAssetUpdated$.pipe(
        skip(1),
        filter(event => assets.some(a => a.id === event.asset.id))
      ),
      this.assetVersionService.onAssetVersionUpdated$.pipe(
        skip(1),
        filter(event => assets.some(a => a.id === event.assetVersion.asset))
      ),
      this.onAssetDeleted$.pipe(
        skip(1),
        filter(event => assets.some(a => a.id === event.asset.id))
      ),
      of({ name: 'initial', asset: undefined as AssetDto })
    ).pipe(
      map(event => {
        if (event.name === 'initial') return { assets, changed: true };
        let changed = false;
        const updatedAsset = event.name === 'updated' && 'asset' in event ? event.asset : undefined;
        const updatedAssetVersion =
          event.name === 'updated' && 'assetVersion' in event ? event.assetVersion : undefined;
        const deletedAsset = event.name === 'deleted' ? event.asset : undefined;

        if (updatedAsset) {
          const oldAsset = assets.find(f => f.id === updatedAsset.id);
          if (oldAsset) {
            changed = true;
            const assetMovedSection = updatedAsset.section !== oldAsset.section;
            if (assetMovedSection) {
              assets = assets.filter(g => g.id !== updatedAsset.id);
            } else {
              const index = assets.findIndex(f => f.id === updatedAsset.id);
              if (index > -1) {
                assets[index] = { ...assets[index], ...updatedAsset };
              }
            }
          }
        }
        if (updatedAssetVersion) {
          const index = assets.findIndex(f => f.id === updatedAssetVersion.asset);
          if (index > -1) {
            assets[index] = {
              ...assets[index],
              version_data: <AssetVersionDto>{
                ...updatedAsset.version_data,
                ...updatedAssetVersion,
              },
            };
          }
        }

        if (deletedAsset) {
          assets = assets.filter(f => f.id !== deletedAsset.id);
          changed = true;
        }

        return { assets, changed };
      }),
      filter(({ changed }) => changed),
      map(({ assets }) => assets)
    );
  }

  private onGetNewAssets(assets: AssetDto[]) {
    if (!assets) return;
    this.onNewAssets$.next(assets);

    assets.forEach(asset => {
      this.assetCache.set(asset.id, asset);
      this.assetGalleryIdMap.set(asset.id, asset.gallery as string);
      if (asset.unseen_activities !== undefined) {
        this.state.unseenActivityCountMap.set(asset.id, asset.unseen_activities);
        delete asset.unseen_activities;
        this.unseenActivityService.registerAssets(asset);
      }
    });
  }

  private getIncludes(includeTags: boolean, includeIsLiked: boolean) {
    let includes = '';
    if (includeTags) includes += '&include[]=tags';
    if (includeIsLiked) includes += '&include[]=is_liked';
    if (this.state.user$.value) includes += '&include[]=unseen_activities';
    return includes;
  }
}
